import React from 'react';
import {
  Button,
  Dialog,
  DialogContent,
  DialogTitle,
  Typography,
  Box,
  DialogActions,
} from '@mui/material';
import { useTranslation } from 'react-i18next';
import { useNavigate } from 'react-router-dom';
import { format, parseISO } from 'date-fns';
import WorkspacePremiumIcon from '@mui/icons-material/WorkspacePremium';
import CalendarTodayIcon from '@mui/icons-material/CalendarToday';
import LabelOutlinedIcon from '@mui/icons-material/LabelOutlined';
import ArrowForwardIcon from '@mui/icons-material/ArrowForward';

export default function PreviousPaidPlanWarning({ open, previousPaidPlan, onClose }) {
  const { t } = useTranslation();
  const navigate = useNavigate();

  if (!open || !previousPaidPlan) return null;

  const formattedExpiry = previousPaidPlan.expire_date
    ? format(parseISO(previousPaidPlan.expire_date), 'dd MMM yyyy')
    : null;

  const handleRenew = () => {
    onClose();
    navigate('/billing');
  };

  return (
    <Dialog open={open} onClose={onClose} maxWidth="sm" fullWidth>
      <DialogTitle sx={{ display: 'flex', alignItems: 'center', gap: 1.5 }}>
        <WorkspacePremiumIcon color="primary" fontSize="large" />
        <Box>
          <Typography variant="h6" sx={{ fontWeight: 700, lineHeight: 1.2 }}>
            {t('renew_plan_warning.welcome_back_premium')}
          </Typography>
          <Typography variant="caption" color="text.secondary">
            {t('renew_plan_warning.previously_enjoyed_paid')}
          </Typography>
        </Box>
      </DialogTitle>

      <DialogContent>
        {/* Previous Plan Card */}
        <Box
          sx={{
            border: '1px solid',
            borderColor: 'divider',
            borderRadius: 2,
            p: 2,
            mb: 2,
            display: 'flex',
            flexDirection: 'column',
            gap: 1,
            bgcolor: 'action.hover',
          }}
        >
          <Box sx={{ display: 'flex', alignItems: 'center', gap: 1 }}>
            <LabelOutlinedIcon fontSize="small" color="action" />
            <Typography variant="body2" color="text.secondary">
              {t('renew_plan_warning.previous_plan_label')}
            </Typography>
            <Typography variant="body2" sx={{ fontWeight: 700 }}>
              {previousPaidPlan.plan_name || `Plan #${previousPaidPlan.plan_id}`}
            </Typography>
          </Box>

          {formattedExpiry && (
            <Box sx={{ display: 'flex', alignItems: 'center', gap: 1 }}>
              <CalendarTodayIcon fontSize="small" color="action" />
              <Typography variant="body2" color="text.secondary">
                {t('renew_plan_warning.expired_on')}
              </Typography>
              <Typography variant="body2" sx={{ fontWeight: 700 }}>
                {formattedExpiry}
              </Typography>
            </Box>
          )}
        </Box>

        <Typography variant="body2" color="text.secondary">
          {t('renew_plan_warning.current_free_plan')}{' '}
          {t('renew_plan_warning.renew_to_regain_access')}
        </Typography>
      </DialogContent>

      <DialogActions sx={{ px: 3, pb: 2.5, gap: 1 }}>
        <Button onClick={onClose} color="inherit">
          {t('renew_plan_warning.maybe_later')}
        </Button>

        <Button
          variant="contained"
          onClick={handleRenew}
          endIcon={<ArrowForwardIcon />}
        >
          {t('renew_plan_warning.renew_plan_and_pay')}
        </Button>
      </DialogActions>
    </Dialog>
  );
}